import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Analytics
import { initAnalytics, trackPageView, trackFeature } from './utils/analytics';

// Core components (loaded immediately)
import Login from './components/Login';
import Navbar from './components/Navbar';
import MobileFooterNav from './components/MobileFooterNav';
import InstallPrompt from './components/InstallPrompt';
import OnboardingTour from './components/OnboardingTour';

// Lazy loaded components (code splitting)
const QuickLinks = lazy(() => import('./components/QuickLinks'));
const Profile = lazy(() => import('./components/Profile'));
const ExpenseForm = lazy(() => import('./components/ExpenseForm'));
const ExpenseTable = lazy(() => import('./components/ExpenseTable'));
const ExpenseSplitting = lazy(() => import('./components/ExpenseSplitting'));
const Notifications = lazy(() => import('./components/Notifications'));
const UserSettings = lazy(() => import('./components/UserSettings'));
const GroupManagement = lazy(() => import('./components/GroupManagement'));
const GroupChat = lazy(() => import('./components/GroupChat'));
const GroupSettings = lazy(() => import('./components/GroupSettings'));
const GroupExpenses = lazy(() => import('./components/GroupExpenses'));
const Settlements = lazy(() => import('./components/Settlements'));
const TravelGuide = lazy(() => import('./components/TravelGuide'));
const Investments = lazy(() => import('./components/Investments'));
const InvestmentAnalytics = lazy(() => import('./components/InvestmentAnalytics'));
const Income = lazy(() => import('./components/Income'));
const CashFlow = lazy(() => import('./components/CashFlow'));
const Budgeting = lazy(() => import('./components/Budgeting'));
const Stats = lazy(() => import('./components/Stats'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const HelpGuide = lazy(() => import('./components/HelpGuide'));
const InvoiceForm = lazy(() => import('./components/InvoiceForm'));
const BillerProfiles = lazy(() => import('./components/BillerProfiles'));
const Business = lazy(() => import('./components/Business'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Route tracker component for analytics
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Map routes to readable page names
    const routeNames = {
      '/': 'Home',
      '/login': 'Login',
      '/quick-links': 'Dashboard',
      '/profile': 'Profile',
      '/expenses': 'Expenses',
      '/expense-list': 'Expenses',
      '/add-expense': 'Add Expense',
      '/split-expense': 'Split Expense',
      '/notifications': 'Notifications',
      '/settings': 'Settings',
      '/settlements': 'Settlements',
      '/groups': 'Groups',
      '/investments': 'Investments',
      '/income': 'Income',
      '/cashflow': 'Cash Flow',
      '/budgeting': 'Budgeting',
      '/investment-analytics': 'Investment Analytics',
      '/stats': 'Stats',
      '/admin': 'Admin Panel'
    };
    
    const pageName = routeNames[location.pathname] || location.pathname;
    trackPageView(pageName);
  }, [location.pathname]);
  
  return null;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set up axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Check if user is logged in on mount
    const initAuth = () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          // Check if onboarding should be shown (for restart tour feature)
          const onboardingComplete = localStorage.getItem('monyply_onboarding_complete');
          if (!onboardingComplete) {
            setTimeout(() => setShowOnboarding(true), 500);
          }
        } catch (e) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  // Initialize analytics when user logs in
  useEffect(() => {
    if (user) {
      initAnalytics();
    }
  }, [user]);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Initialize analytics after login
    setTimeout(() => initAnalytics(), 100);
    
    // Check if first time user - show onboarding tour
    const onboardingComplete = localStorage.getItem('monyply_onboarding_complete');
    if (!onboardingComplete) {
      setTimeout(() => setShowOnboarding(true), 500);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, API, trackFeature }}>
      <Router>
        {user && showOnboarding && <OnboardingTour onComplete={handleOnboardingComplete} />}
        {user && <RouteTracker />}
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 overflow-x-hidden">
          {user && <Navbar />}
          <div className={user ? "pt-14 sm:pt-16" : ""}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route
                  path="/login"
                  element={user ? <Navigate to="/quick-links" /> : <Login />}
                />
                <Route
                  path="/"
                  element={user ? <Navigate to="/quick-links" /> : <Navigate to="/login" />}
                />
                <Route
                  path="/quick-links"
                  element={user ? <QuickLinks /> : <Navigate to="/login" />}
                />
                <Route
                  path="/profile"
                  element={user ? <Profile /> : <Navigate to="/login" />}
                />
              {/* Expense Tile View removed - redirecting to Spreadsheet view */}
              <Route
                path="/expenses"
                element={<Navigate to="/expense-list" replace />}
              />
              <Route
                path="/expense-list"
                element={user ? <ExpenseTable /> : <Navigate to="/login" />}
              />
              <Route
                path="/add-expense"
                element={user ? <ExpenseForm /> : <Navigate to="/login" />}
              />
              {/* OCR Feature Removed - Phase 4 */}
              {/* <Route
                path="/scan-invoice"
                element={user ? <InvoiceScanner /> : <Navigate to="/login" />}
              /> */}
              <Route
                path="/split-expense"
                element={user ? <ExpenseSplitting /> : <Navigate to="/login" />}
              />
              {/* Budget Feature Removed */}
              {/* <Route
                path="/budgets"
                element={user ? <BudgetManager /> : <Navigate to="/login" />}
              /> */}
              <Route
                path="/notifications"
                element={user ? <Notifications /> : <Navigate to="/login" />}
              />
              <Route
                path="/settings"
                element={user ? <UserSettings /> : <Navigate to="/login" />}
              />
              <Route
                path="/settlements"
                element={user ? <Settlements /> : <Navigate to="/login" />}
              />
              <Route
                path="/groups"
                element={user ? <GroupManagement /> : <Navigate to="/login" />}
              />
              <Route
                path="/groups/:groupId/chat"
                element={user ? <GroupChat /> : <Navigate to="/login" />}
              />
              <Route
                path="/groups/:groupId/expenses"
                element={user ? <GroupExpenses /> : <Navigate to="/login" />}
              />
              <Route
                path="/groups/:groupId/settings"
                element={user ? <GroupSettings /> : <Navigate to="/login" />}
              />
              <Route
                path="/groups/:groupId/travel"
                element={user ? <TravelGuide /> : <Navigate to="/login" />}
              />
              <Route
                path="/investments"
                element={user ? <Investments /> : <Navigate to="/login" />}
              />
              <Route
                path="/income"
                element={user ? <Income /> : <Navigate to="/login" />}
              />
              {/* InflowOutflow Tile View removed - redirecting to Spreadsheet view */}
              <Route
                path="/inflow-outflow"
                element={<Navigate to="/cashflow" replace />}
              />
              <Route
                path="/cashflow"
                element={user ? <CashFlow /> : <Navigate to="/login" />}
              />
              <Route
                path="/budgeting"
                element={user ? <Budgeting /> : <Navigate to="/login" />}
              />
              <Route
                path="/investment-analytics"
                element={user ? <InvestmentAnalytics /> : <Navigate to="/login" />}
              />
              <Route
                path="/stats"
                element={user ? <Stats /> : <Navigate to="/login" />}
              />
              <Route
                path="/admin"
                element={user ? <AdminDashboard /> : <Navigate to="/login" />}
              />
              <Route
                path="/help"
                element={user ? <HelpGuide /> : <Navigate to="/login" />}
              />
              <Route
                path="/invoices"
                element={user ? <Navigate to="/business?tab=invoices" /> : <Navigate to="/login" />}
              />
              <Route
                path="/business"
                element={user ? <Business /> : <Navigate to="/login" />}
              />
              <Route
                path="/invoices/new"
                element={user ? <InvoiceForm /> : <Navigate to="/login" />}
              />
              <Route
                path="/invoices/:id/edit"
                element={user ? <InvoiceForm /> : <Navigate to="/login" />}
              />
              <Route
                path="/biller-profiles"
                element={user ? <BillerProfiles /> : <Navigate to="/login" />}
              />
            </Routes>
            </Suspense>
          </div>
          {/* Mobile Footer Navigation - only visible on mobile */}
          {user && <MobileFooterNav />}
          {/* PWA Install Prompt - shown to all users on mobile for auto-install */}
          <InstallPrompt />
          {/* Bottom padding spacer for mobile to account for fixed footer */}
          {user && <div className="md:hidden h-20" />}
          <Toaster position="top-right" />
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;